"use client";
import React, { useEffect, useState } from "react";
import { Block, blockStyles } from "../types/screenplay-editor.types";
import { Loader2, Tags, AlignLeft, ChevronDown, X } from "lucide-react";
import {
  ElementCategory,
  Tag,
  CATEGORIES,
} from "../types/script-breakdown.types";
import { Header } from "../components/header";
import { crowdAgeOption, crowdTypeOption } from "@/data/crowd-data";
import { MultiSelect } from "../components/input/inputs";

export function ScriptBreakdownSceneView({
  blocks,
  characters,
  isLoading,
  sceneNumber,
  tags,
  selectionMenu,
  handleMouseUp,
  addTag,
  updateTag,
  removeTag,
  clearSelection,
  menuPlacement,
  menuRef,
  sceneBrief,
  setSceneBrief,
  onSummarize,
  isSummarizing,
  aiSummarized = false,
  onUpdateBrief,
  headerChildren,
}: {
  blocks: Block[];
  characters: string[];
  isLoading: boolean;
  sceneNumber: string;
  tags: Tag[];
  selectionMenu: {
    blockId: string;
    startIndex: number;
    endIndex: number;
    text: string;
    top: number;
    left: number;
  } | null;
  handleMouseUp: () => void;
  addTag: (c: ElementCategory) => void;
  updateTag?: (
    id: string,
    categoryId?: ElementCategory | null,
    details?: Partial<Tag>,
  ) => void;
  removeTag: (e: React.MouseEvent, id?: string) => void;
  clearSelection: () => void;
  menuPlacement: "top" | "bottom";
  menuRef: React.RefObject<HTMLDivElement | null>;
  sceneBrief: string;
  setSceneBrief: (brief: string) => void;
  onSummarize?: () => void;
  isSummarizing?: boolean;
  aiSummarized?: boolean;
  onUpdateBrief?: () => void;
  headerChildren?: React.ReactNode;
}) {
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [editingTagData, setEditingTagData] = useState<{
    tag: Tag;
    catId: ElementCategory;
    catColor: string;
    rect: DOMRect;
  } | null>(null);
  const [tagForm, setTagForm] = useState<{
    quantity: number;
    look: string;
    age: string;
    age_range: { name: string }[];
    crowd_type: { name: string }[];
  }>({ quantity: 1, look: "", age: "", age_range: [], crowd_type: [] });
  const [popupPlacement, setPopupPlacement] = useState({
    alignRight: false,
    alignBottom: false,
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const COURIER_STACK = "'Courier Prime', 'Courier', monospace";

  useEffect(() => {
    const fontId = "google-font-courier-prime";
    const styleId = "screenplay-editor-force-v4";

    // 1. Inject Google Font
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }

    // 2. Inject high-specificity CSS (Safe textContent approach)
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        /* We target by the data-attribute to ensure the highest specificity possible */
        [data-screenplay-editor] *, 
        [data-screenplay-editor] div, 
        [data-screenplay-editor] span, 
        [data-screenplay-editor] [contenteditable="true"] {
          font-family: ${COURIER_STACK} !important;
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(style);
    }
  }, [COURIER_STACK]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        <p className="text-sm font-medium text-zinc-500 animate-pulse">
          Loading scene details...
        </p>
      </div>
    );
  }

  const hasLocationTag = tags.some((t) => t.category_id === "LOCATION");

  const renderBlockText = (block: Block) => {
    const blockTags = tags
      .filter((t) => t.block_id === block.id)
      .sort((a, b) => a.start_index - b.start_index);

    if (blockTags.length === 0) return block.text;

    const nodes = [];
    let currentIndex = 0;

    blockTags.forEach((tag) => {
      // Defensively prevent regressing indexes if tags overlap
      const actualStart = Math.max(tag.start_index, currentIndex);

      if (actualStart > currentIndex) {
        nodes.push(
          <span key={`text-${currentIndex}`}>
            {block.text.slice(currentIndex, actualStart)}
          </span>,
        );
      }

      const category = CATEGORIES.find((c) => c.id === tag.category_id);
      if (actualStart < tag.end_index) {
        nodes.push(
          <span
            key={tag.id || `tag-${actualStart}-${tag.end_index}`}
            title={`${category?.label} (Click to edit)`}
            onClick={(e) => {
              e.stopPropagation();
              const selection = window.getSelection();
              if (!selection) return;
              const range = document.createRange();

              // Select the inner text node so the range offset reflects the text length
              // rather than the child node count (which was causing the 1-character bug)
              const textNode = e.currentTarget.firstChild;
              if (textNode && textNode.nodeType === Node.TEXT_NODE) {
                range.selectNodeContents(textNode);
              } else {
                range.selectNodeContents(e.currentTarget);
              }

              selection.removeAllRanges();
              selection.addRange(range);
              setTimeout(() => handleMouseUp(), 0);
            }}
            className="cursor-pointer font-bold transition-all hover:opacity-80 rounded-[3px]"
            style={{
              color: category?.color,
              padding: "0.125rem 0.25rem",
              margin: "0 -0.125rem",
            }}
          >
            {block.text.slice(actualStart, tag.end_index)}
          </span>,
        );
      }

      currentIndex = Math.max(currentIndex, tag.end_index);
    });

    if (currentIndex < block.text.length) {
      nodes.push(
        <span key={`text-${currentIndex}`}>
          {block.text.slice(currentIndex)}
        </span>,
      );
    }

    return nodes;
  };

  return (
    <div className="relative flex items-center justify-center w-full">
      <div className="relative w-full min-h-screen flex flex-col xl:flex-row bg-zinc-50/20 transition-all duration-300">
        <div className="flex-1 flex flex-col min-w-0">
          <Header isSummarizing={isSummarizing} onSummarize={onSummarize}>
            {headerChildren}
          </Header>

          <div className="relative p-8 md:p-12 mx-auto w-full flex flex-col gap-8">
            <div className="flex items-center justify-center max-w-6xl xl:max-w-full">
              <div className="flex flex-col gap-4 min-h-screen">
                <div
                  className="relative bg-[#fdfdfc] shadow-md shadow-zinc-300/60 border border-zinc-100 rounded-sm md:rounded-md pl-[1.5in] py-[1in] pr-[1in] flex flex-col w-[210mm] min-h-auto shrink-0"
                  style={{
                    fontFamily: COURIER_STACK,
                    paddingLeft: "1.5in",
                    paddingRight: "1in",
                    paddingTop: "1in",
                    paddingBottom: "1in",
                    lineHeight: "1.2",
                  }}
                  data-screenplay-editor="true"
                  onMouseUp={handleMouseUp}
                >
                  {blocks.map((block) => (
                    <div
                      key={block.id}
                      data-block-id={block.id}
                      className={`relative break-words w-full px-4 py-2 ${blockStyles[block.type].className}`}
                      style={{
                        ...blockStyles[block.type].inputStyle,
                        minHeight: "2.5rem",
                      }}
                    >
                      {renderBlockText(block)}

                      {selectionMenu?.blockId === block.id && (
                        <div
                          ref={menuRef}
                          data-screenplay-editor="false"
                          className={`tag-menu absolute z-50 bg-white/70 backdrop-blur-2xl shadow-[0_10px_40px_rgb(0,0,0,0.06)] border border-white rounded-[1.5rem] p-2 flex flex-col w-56 animate-in fade-in zoom-in-95 duration-300 ease-out ${menuPlacement === "top" ? "origin-bottom" : "origin-top"}`}
                          style={{
                            top: selectionMenu.top,
                            left: selectionMenu.left,
                            transform:
                              menuPlacement === "top"
                                ? "translate(-50%, calc(-100% - 12px))"
                                : "translate(-50%, 32px)",
                          }}
                        >
                          <div className="relative z-10 px-3 py-2.5 border-b border-white/60 mb-1.5">
                            <p className="text-[9px] font-extrabold tracking-[0.2em] text-slate-400 uppercase mb-1">
                              Tag Element
                            </p>
                            <p
                              className="text-xs font-bold text-slate-700 truncate drop-shadow-sm"
                              title={selectionMenu.text}
                            >
                              "{selectionMenu.text}"
                            </p>
                          </div>
                          <div className="relative z-10 flex flex-col gap-1">
                            {CATEGORIES.filter(
                              (cat) =>
                                !(cat.id === "LOCATION" && hasLocationTag) &&
                                !(cat.id === "SUBLOCATION" && !hasLocationTag),
                            ).map((cat) => (
                              <button
                                key={cat.id}
                                onClick={() => {
                                  const existingTag = tags.find(
                                    (t) =>
                                      t.block_id === block.id &&
                                      t.start_index ===
                                        selectionMenu.startIndex &&
                                      t.end_index === selectionMenu.endIndex,
                                  );
                                  if (existingTag && existingTag.id) {
                                    updateTag?.(existingTag.id, cat.id);
                                  } else {
                                    addTag(cat.id);
                                  }
                                }}
                                className="group w-full text-[12px] font-bold px-3 py-2 rounded-xl transition-all duration-300 text-left flex items-center justify-between hover:bg-white/80 hover:shadow-[0_2px_10px_rgb(0,0,0,0.02)] active:scale-[0.98]"
                                style={{ color: cat.color }}
                              >
                                <div className="flex items-center gap-3">
                                  <cat.icon
                                    className="w-4 h-4 group-hover:scale-110 transition-transform duration-300"
                                    style={{ color: cat.color }}
                                  />
                                  {cat.label}
                                </div>
                                <span className="text-[10px] font-bold text-slate-400 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-1 group-hover:translate-x-0">
                                  Select
                                </span>
                              </button>
                            ))}

                            {tags.some(
                              (t) =>
                                t.block_id === block.id &&
                                t.start_index === selectionMenu.startIndex &&
                                t.end_index === selectionMenu.endIndex,
                            ) && (
                              <div className="mt-1 pt-1 border-t border-white/60">
                                <button
                                  onClick={(e) => {
                                    const tagToRemove = tags.find(
                                      (t) =>
                                        t.block_id === block.id &&
                                        t.start_index ===
                                          selectionMenu.startIndex &&
                                        t.end_index === selectionMenu.endIndex,
                                    );
                                    if (tagToRemove) {
                                      removeTag(e, tagToRemove.id);
                                      clearSelection();
                                    }
                                  }}
                                  className="group w-full text-[12px] font-bold px-3 py-2 rounded-xl transition-all duration-300 text-left flex items-center justify-between hover:bg-rose-50 hover:text-rose-600 hover:shadow-[0_2px_10px_rgb(225,29,72,0.04)] active:scale-[0.98] text-slate-500 border border-transparent hover:border-rose-100"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 rounded-full shadow-sm bg-rose-400 group-hover:scale-125 transition-transform duration-300" />
                                    Remove Tag
                                  </div>
                                  <span className="text-[10px] font-bold text-rose-400 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-1 group-hover:translate-x-0">
                                    Remove
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="relative bg-[#fdfdfc] shadow-2xl shadow-zinc-300/50 border border-zinc-100/80 rounded-sm md:rounded-md flex flex-col w-[210mm] shrink-0 p-8 md:p-12 lg:p-16">
                  <h3 className="text-xs font-bold text-blumine-800 uppercase tracking-widest mb-6 flex items-center gap-3 font-sans">
                    <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-gradient-to-b from-white to-blumine-50/60 shadow-inner border border-blumine-200/50 text-blumine-600">
                      <AlignLeft className="w-4 h-4" />
                    </span>
                    Scene Brief
                  </h3>
                  <div className="relative bg-zinc-50/30 border border-zinc-200/50 rounded-xl p-4 md:p-6 shadow-[inset_0_1.5px_3px_rgba(0,0,0,0.02)] focus-within:bg-white focus-within:border-blumine-300/70 focus-within:shadow-[0_8px_30px_rgba(16,43,60,0.03)] transition-all duration-300">
                    <textarea
                      value={sceneBrief}
                      onChange={(e) => setSceneBrief(e.target.value)}
                      placeholder="Write a brief description or notes for this scene..."
                      className="w-full min-h-[120px] bg-transparent outline-none resize-y text-zinc-700 placeholder:text-zinc-400 text-sm md:text-base custom-scrollbar font-sans select-none"
                      style={{
                        lineHeight: "1.6",
                      }}
                    />
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={onUpdateBrief}
                        disabled={!sceneBrief || sceneBrief.trim() === ""}
                        className="rounded-xl bg-gradient-to-b from-blumine-500 to-blumine-600 px-6 py-2.5 text-[13px] font-bold text-white shadow-md shadow-blumine-500/10 transition-all hover:from-blumine-600 hover:to-blumine-700 active:scale-95 disabled:from-zinc-100 disabled:to-zinc-200 disabled:text-zinc-400 disabled:border-zinc-200/50 disabled:shadow-none disabled:cursor-not-allowed cursor-pointer"
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-b from-blumine-500 to-blumine-600 text-white shadow-[0_8px_30px_rgba(32,146,186,0.35)] transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-[0_10px_35px_rgba(32,146,186,0.45)] xl:hidden border border-white/10"
          >
            <Tags className="h-5.5 w-5.5" strokeWidth={2} />
            {tags.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-md border-2 border-white animate-pulse">
                {tags.length}
              </span>
            )}
          </button>
        </div>
        {isSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-blumine-950/20 backdrop-blur-xs transition-all duration-300 xl:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        <div
          className={`fixed xl:sticky top-0 right-0 z-50 h-screen min-h-screen w-80 shrink-0 transform transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            tags.length > 0 ? "xl:block" : "xl:hidden"
          } ${
            isSidebarOpen
              ? "translate-x-0 shadow-[-10px_0_40px_rgba(16,43,60,0.08)]"
              : "max-xl:translate-x-full"
          }`}
        >
          <div className="flex h-full w-full flex-col border-l border-blumine-100/50 bg-white/95 backdrop-blur-xl p-4 py-6">
            {/* {!aiSummarized && (
              <div className="mb-4 shrink-0 flex justify-center">
                <SummarizeButton
                  isSummarizing={isSummarizing}
                  onSummarize={onSummarize}
                />
              </div>
            )} */}

            <div className="relative flex flex-col gap-3 flex-1 overflow-hidden">
              {/* Sidebar Header Section */}
              <div className="relative flex items-center justify-between mb-4 shrink-0 px-1">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-blumine-200/60 bg-gradient-to-b from-white to-blumine-50/50 shadow-inner">
                    <Tags
                      className="h-5 w-5 text-blumine-600"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-bold uppercase tracking-wider text-blumine-800 leading-none">
                      Breakdown
                    </h3>
                    <p className="text-[11px] text-blumine-400 mt-1 font-medium">
                      Scene element tags
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-blumine-200/50 bg-blumine-50/80 px-2.5 py-0.5 text-[11px] font-bold text-blumine-700 shadow-sm">
                    {tags.length}
                  </span>

                  {/* Elegant close button for mobile screens */}
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="flex xl:hidden h-8 w-8 items-center justify-center rounded-lg text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700 transition duration-150 active:scale-95"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Scrollable breakdown content */}
              <div className="flex flex-col gap-3.5 overflow-y-auto overflow-x-hidden pb-4 pr-1 custom-scrollbar">
                {CATEGORIES.map((cat) => {
                  const catTags = tags.filter((t) => t.category_id === cat.id);
                  if (!catTags.length) return null;

                  const isExpanded = expandedCategories[cat.id];

                  const uniqueTags = Array.from(
                    new Map(
                      catTags.map((tag) => [tag.name.toLowerCase(), tag]),
                    ).values(),
                  );

                  return (
                    <div key={cat.id} className="relative">
                      {/* Premium Accordion Header */}
                      <button
                        onClick={() =>
                          setExpandedCategories((prev) => ({
                            [cat.id]: !prev[cat.id],
                          }))
                        }
                        className="w-full group relative flex cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3 transition-all duration-300 hover:-translate-y-[1px] hover:shadow-md active:scale-[0.98] text-left outline-none"
                        style={{
                          borderColor: `${cat.color}25`,
                          background: `linear-gradient(135deg, #ffffff, ${cat.color}08)`,
                        }}
                      >
                        {/* Subtle glow highlight on hover */}
                        <div
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                          style={{
                            background: `radial-gradient(circle at 12% 50%, ${cat.color}15, transparent 40%)`,
                          }}
                        />

                        <div
                          className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/80 shadow-inner transition-transform duration-300 group-hover:scale-105"
                          style={{
                            background: `linear-gradient(135deg, ${cat.color}12, ${cat.color}05)`,
                            borderColor: `${cat.color}20`,
                          }}
                        >
                          <cat.icon
                            className="h-4 w-4"
                            style={{ color: cat.color }}
                          />
                        </div>

                        <span
                          className="relative text-xs font-bold tracking-wide transition-colors duration-200"
                          style={{ color: cat.color }}
                        >
                          {cat.label}
                        </span>

                        <div className="ml-auto flex items-center gap-1.5 relative">
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-bold transition-all duration-200"
                            style={{
                              color: cat.color,
                              backgroundColor: `${cat.color}10`,
                              border: `1px solid ${cat.color}15`,
                            }}
                          >
                            {catTags.length}
                          </span>
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                            style={{ color: cat.color }}
                            strokeWidth={2.5}
                          />
                        </div>
                      </button>

                      {/* Premium Accordion Body */}
                      <div
                        className={`grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isExpanded
                            ? "grid-rows-[1fr] opacity-100 mt-2.5"
                            : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div
                            className="flex flex-wrap justify-start gap-2 py-2 pl-3 border-l ml-4"
                            style={{ borderColor: `${cat.color}18` }}
                          >
                            {uniqueTags.map((tag, index) => (
                              <div
                                key={index}
                                className="relative flex flex-col items-start max-w-full"
                              >
                                <span
                                  title={tag.name}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      cat.id !== "PROP" &&
                                      cat.id !== "SET_PROP" &&
                                      cat.id !== "VEHICLE" &&
                                      cat.id !== "EXTRA"
                                    )
                                      return;
                                    if (editingTagData?.tag.id === tag.id) {
                                      setEditingTagData(null);
                                    } else {
                                      const rect =
                                        e.currentTarget.getBoundingClientRect();
                                      setPopupPlacement({
                                        alignRight:
                                          rect.left > window.innerWidth - 240,
                                        alignBottom:
                                          rect.bottom >
                                          window.innerHeight - 250,
                                      });
                                      setTagForm({
                                        quantity: tag.quantity || 1,
                                        look: tag.look || "",
                                        age: tag.age || "",
                                        age_range: tag.age_range || [],
                                        crowd_type: tag.crowd_type || [],
                                      });
                                      setEditingTagData({
                                        tag,
                                        catId: cat.id,
                                        catColor: cat.color,
                                        rect,
                                      });
                                    }
                                  }}
                                  className={`inline-block max-w-full truncate text-[11px] font-bold px-3 py-1.5 rounded-full border transition-all duration-200 select-none ${
                                    cat.id === "PROP" ||
                                    cat.id === "SET_PROP" ||
                                    cat.id === "VEHICLE" ||
                                    cat.id === "EXTRA"
                                      ? "cursor-pointer hover:scale-[1.03] hover:shadow-sm"
                                      : "cursor-default"
                                  }`}
                                  style={{
                                    color: cat.color,
                                    background: `linear-gradient(135deg, ${cat.color}08, ${cat.color}03)`,
                                    borderColor: `${cat.color}18`,
                                  }}
                                >
                                  {tag.name}
                                  {(cat.id === "PROP" ||
                                    cat.id === "SET_PROP" ||
                                    cat.id === "VEHICLE") &&
                                  tag.quantity &&
                                  tag.quantity > 1 ? (
                                    <span
                                      className="ml-1.5 font-extrabold px-1 py-0.5 rounded text-[9px] border inline-block"
                                      style={{
                                        color: cat.color,
                                        backgroundColor: `${cat.color}10`,
                                        borderColor: `${cat.color}15`,
                                      }}
                                    >
                                      x{tag.quantity}
                                    </span>
                                  ) : null}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {editingTagData && (
          <>
            <div
              className="fixed inset-0 z-[100] bg-blumine-950/5 backdrop-blur-[1px]"
              onClick={(e) => {
                e.stopPropagation();
                setEditingTagData(null);
              }}
            />
            <div
              className={`p-3.5 bg-white/90 backdrop-blur-2xl rounded-[1.25rem] shadow-[0_10px_40px_rgba(16,43,60,0.12)] border border-blumine-100/40 z-[101] w-56 animate-in fade-in zoom-in-95 duration-200 ease-out fixed`}
              style={{
                top: popupPlacement.alignBottom
                  ? "auto"
                  : editingTagData.rect.bottom + 8,
                bottom: popupPlacement.alignBottom
                  ? window.innerHeight - editingTagData.rect.top + 8
                  : "auto",
                left: popupPlacement.alignRight
                  ? "auto"
                  : editingTagData.rect.left,
                right: popupPlacement.alignRight
                  ? window.innerWidth - editingTagData.rect.right
                  : "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-3 border-b border-blumine-100/30 pb-2.5">
                <span className="text-[10px] font-extrabold text-blumine-400 uppercase tracking-widest drop-shadow-sm">
                  Details
                </span>
                <button
                  onClick={() => setEditingTagData(null)}
                  className="hover:bg-blumine-100/40 p-1.5 rounded-full transition-colors active:scale-95 text-blumine-500"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {(editingTagData.catId === "PROP" ||
                editingTagData.catId === "SET_PROP" ||
                editingTagData.catId === "VEHICLE") && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-bold text-blumine-700 ml-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={tagForm.quantity ?? ""}
                    min={1}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || val === "-") {
                        setTagForm((prev) => ({
                          ...prev,
                          quantity: val as any,
                        }));
                      } else {
                        setTagForm((prev) => ({
                          ...prev,
                          quantity: parseInt(val),
                        }));
                      }
                    }}
                    onBlur={() => {
                      const q: any = tagForm.quantity;
                      if (
                        q === "" ||
                        q === "-" ||
                        q === undefined ||
                        Number.isNaN(q) ||
                        q < 1
                      ) {
                        setTagForm((prev) => ({ ...prev, quantity: 1 }));
                      }
                    }}
                    className="w-full text-[13px] px-3 py-2 rounded-xl border border-blumine-100 bg-zinc-50/30 outline-none focus:border-blumine-400 focus:bg-white focus:ring-2 focus:ring-blumine-500/10 transition-all font-semibold text-blumine-800"
                  />
                </div>
              )}
              {editingTagData.catId === "EXTRA" && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-bold text-blumine-700 ml-1">
                      Count
                    </label>
                    <input
                      type="number"
                      value={tagForm.quantity ?? ""}
                      min={1}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || val === "-") {
                          setTagForm((prev) => ({
                            ...prev,
                            quantity: val as any,
                          }));
                        } else {
                          setTagForm((prev) => ({
                            ...prev,
                            quantity: parseInt(val),
                          }));
                        }
                      }}
                      onBlur={() => {
                        const q: any = tagForm.quantity;
                        if (
                          q === "" ||
                          q === "-" ||
                          q === undefined ||
                          Number.isNaN(q) ||
                          q < 1
                        ) {
                          setTagForm((prev) => ({ ...prev, quantity: 1 }));
                        }
                      }}
                      className="w-full text-[13px] px-3 py-2 rounded-xl border border-blumine-100 bg-zinc-50/30 outline-none focus:border-blumine-400 focus:bg-white focus:ring-2 focus:ring-blumine-500/10 transition-all font-semibold text-blumine-800"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-bold text-blumine-700 ml-1">
                      Look
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Angry, Cheering"
                      value={tagForm.look}
                      onChange={(e) => {
                        setTagForm((prev) => ({
                          ...prev,
                          look: e.target.value,
                        }));
                      }}
                      className="w-full text-[13px] px-3 py-2 rounded-xl border border-blumine-100 bg-zinc-50/30 outline-none focus:border-blumine-400 focus:bg-white focus:ring-2 focus:ring-blumine-500/10 transition-all font-semibold text-blumine-800 placeholder:text-blumine-300 placeholder:font-normal"
                    />
                  </div>
                  <MultiSelect
                    label="Age Range"
                    options={crowdAgeOption}
                    value={tagForm.age_range}
                    returnObjects={true}
                    onChange={(val) =>
                      setTagForm((prev) => ({ ...prev, age_range: val }))
                    }
                  />
                  <MultiSelect
                    label="Crowd Type"
                    options={crowdTypeOption}
                    value={tagForm.crowd_type}
                    returnObjects={true}
                    onChange={(val) =>
                      setTagForm((prev) => ({ ...prev, crowd_type: val }))
                    }
                  />
                </div>
              )}
              <div className="mt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editingTagData.tag.id) {
                      updateTag?.(editingTagData.tag.id, editingTagData.catId, {
                        ...(editingTagData.catId === "PROP" ||
                        editingTagData.catId === "SET_PROP" ||
                        editingTagData.catId === "VEHICLE"
                          ? { quantity: tagForm.quantity }
                          : {}),
                        ...(editingTagData.catId === "EXTRA"
                          ? {
                              age_range: tagForm.age_range,
                              crowd_type: tagForm.crowd_type,
                              quantity: tagForm.quantity,
                              look: tagForm.look,
                            }
                          : {}),
                      });
                    }
                    setEditingTagData(null);
                  }}
                  className="w-full bg-gradient-to-b from-blumine-500 to-blumine-600 hover:from-blumine-600 hover:to-blumine-700 text-white text-[12px] font-bold py-2.5 rounded-xl transition-all shadow-[0_4px_12px_rgba(32,146,186,0.2)] hover:shadow-[0_6px_16px_rgba(32,146,186,0.3)] active:scale-[0.98]"
                >
                  Update Details
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
